/**
 * Where the visitor was headed before the sign-in gate stopped them.
 *
 * The router already carries this in `location.state`, and for an uninterrupted popup
 * sign-in that is enough — nothing reloads, so the state is still there when the customer
 * comes back. It stops being enough the moment the tab reloads, and a shared order link
 * is exactly the case where it does:
 *
 *   - The link is tapped inside Messenger or Instagram, where Google refuses OAuth. The
 *     customer is told to reopen it in a real browser, and that browser starts cold.
 *   - The sign-in screen is refreshed while they go hunting for their Google password.
 *   - A phone with little memory evicts the backgrounded tab while the Google window is
 *     in front, and restores it from the URL alone.
 *
 * Each of those lands back on "/" with an empty history state, and the destination they
 * actually asked for is gone — so they finish signing in and get dropped on Home with no
 * idea what happened to the link somebody sent them.
 *
 * sessionStorage survives all three and dies with the tab, which is the exact lifetime an
 * "I was going here" note should have. It is a backup for the router state, not a
 * replacement: `location.state` is still read first because it is the freshest.
 */

const KEY = 'machi.user.returnTo'

/**
 * Internal paths only, and never "/" itself.
 *
 * Anything read back out of storage is untrusted input — the same XSS that would steal the
 * token in localStorage could plant a destination here — and both "//evil.example" and
 * "https://evil.example" are strings `navigate()` will happily leave the site for. So the
 * shape is checked rather than assumed. "/" is refused because sending somebody there
 * after sign-in returns them to the screen they just left, which reads as a failed login.
 */
function isInternalPath(path) {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    path !== '/'
  )
}

/**
 * A location object flattened to one navigable string.
 *
 * Query and hash come along with the path: `/order/pat-rider?ref=fb` and a link into an
 * anchored section are both destinations somebody chose, and dropping either half of them
 * is the same bug in a smaller form.
 */
export function toPath(location) {
  if (!location) return null

  const path = `${location.pathname ?? ''}${location.search ?? ''}${location.hash ?? ''}`

  return isInternalPath(path) ? path : null
}

/**
 * Remember a destination. Storage failing is not worth an error: in a browser with
 * storage switched off the visitor simply lands on Home, which is the behaviour we had
 * before this file existed.
 */
export function rememberDestination(location) {
  const path = toPath(location)

  if (!path) return

  try {
    sessionStorage.setItem(KEY, path)
  } catch {
    // Private mode, storage disabled, quota. Nothing to do and nothing to say.
  }
}

/**
 * Read the destination and clear it in the same breath.
 *
 * Clearing on read rather than on use is deliberate. A note left behind after one sign-in
 * would fire again on the next one in the same tab — sign out, sign back in, and get
 * thrown at an order form you finished twenty minutes ago.
 */
export function takeDestination() {
  let stored = null

  try {
    stored = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch {
    return null
  }

  return isInternalPath(stored) ? stored : null
}

/** Drop the note without following it — used when a session ends on purpose. */
export function forgetDestination() {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // As above.
  }
}
